import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    Text,
    Input,
    Button,
    Stack,
    Select,
    FormControl,
    FormLabel,
    Wrap,
    WrapItem,
    Tag,
    TagLabel,
    TagCloseButton,
    useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { SupabaseClient } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';

// Reusable TagInput component
const TagInput = React.memo(({ label, tags, onAdd, onRemove }) => {
    const [inputValue, setInputValue] = useState('');
    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const trimmed = inputValue.trim();
                if (trimmed) {
                    onAdd(trimmed);
                    setInputValue('');
                }
            }
        },
        [inputValue, onAdd]
    );

    return (
        <FormControl>
            <FormLabel>{label}</FormLabel>
            <Input
                placeholder={label}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <Wrap mt={2} spacing={2}>
                {tags.map((item, idx) => (
                    <WrapItem key={idx}>
                        <Tag size="md" borderRadius="full">
                            <TagLabel>{item}</TagLabel>
                            <TagCloseButton onClick={() => onRemove(idx)} />
                        </Tag>
                    </WrapItem>
                ))}
            </Wrap>
        </FormControl>
    );
});

// Weight input
const WeightInput = ({ weight, weightUnit, onChange }) => (
    <FormControl isRequired>
        <FormLabel>Weight</FormLabel>
        <Stack direction="row" spacing={3}>
            <Input
                name="weight"
                type="number"
                value={weight}
                onChange={(e) => onChange('weight', e.target.value)}
            />
            <Select
                name="weightUnit"
                value={weightUnit}
                onChange={(e) => onChange('weightUnit', e.target.value)}
            >
                <option value="kg">kg</option>
                <option value="lbs">lbs</option>
            </Select>
        </Stack>
    </FormControl>
);

// Height input
const HeightInput = ({ heightUnit, height, feet, inches, onChange }) => (
    <FormControl isRequired>
        <FormLabel>Height</FormLabel>
        <Select
            name="heightUnit"
            value={heightUnit}
            onChange={(e) => onChange('heightUnit', e.target.value)}
            mb={2}
        >
            <option value="cm">cm</option>
            <option value="ft">ft</option>
        </Select>

        {heightUnit === 'cm' ? (
            <Input
                name="height"
                type="number"
                value={height}
                onChange={(e) => onChange('height', e.target.value)}
            />
        ) : (
            <Stack direction="row" spacing={3}>
                <Input
                    name="feet"
                    type="number"
                    placeholder="Feet"
                    value={feet}
                    onChange={(e) => onChange('feet', e.target.value)}
                />
                <Input
                    name="inches"
                    type="number"
                    placeholder="Inches"
                    value={inches}
                    onChange={(e) => onChange('inches', e.target.value)}
                />
            </Stack>
        )}
    </FormControl>
);

const SetupProfilePage = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const toast = useToast();

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        weight: '',
        weightUnit: 'kg',
        heightUnit: 'cm',
        height: '',
        feet: '',
        inches: '',
        dietary_restrictions: '',
        current_medications: [],
        pre_existing_conditions: [],
        fitness_goals: [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Fetch profile
    useEffect(() => {
        if (!user) return;
        SupabaseClient.from('profiles')
            .select('*')
            .eq('id', user.id)
            .single()
            .then(({ data }) => {
                if (data) setFormData((prev) => ({ ...prev, ...data }));
            });
    }, [user]);

    const handleFieldChange = useCallback((name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const addTag = useCallback(
        (field) => (value) => {
            setFormData((prev) => ({ ...prev, [field]: [...prev[field], value] }));
        },
        []
    );

    const removeTag = useCallback(
        (field) => (idx) => {
            setFormData((prev) => ({ ...prev, [field]: prev[field].filter((_, i) => i !== idx) }));
        },
        []
    );

    const convertWeight = (val, unit) => (unit === 'lbs' ? val * 0.453592 : val);
    const convertHeight = (val, unit, ft, inch) =>
        unit === 'ft' ? ft * 30.48 + inch * 2.54 : val;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        const { first_name, last_name, date_of_birth } = formData;
        if (!first_name || !last_name || !date_of_birth) {
            toast({
                title: t('setupProfile.toast.validationTitle'),
                description: t('setupProfile.toast.validation'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        setError('');

        const payload = {
            id: user.id,
            ...formData,
            weight: convertWeight(+formData.weight, formData.weightUnit),
            height: convertHeight(
                +formData.height,
                formData.heightUnit,
                +formData.feet,
                +formData.inches
            ),
        };

        const { error: upsertError } = await SupabaseClient.from('profiles').upsert([payload], {
            onConflict: ['id'],
        });

        setLoading(false);
        if (upsertError) {
            setError(upsertError.message);
            toast({
                title: t('setupProfile.toast.errorTitle'),
                description: upsertError.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } else {
            toast({
                title: t('setupProfile.toast.saveSuccess'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box maxW="md" mx="auto" mt={10} p={6} borderWidth="1px" borderRadius="md" boxShadow="md">
            <Heading mb={4}>{t('setupProfile.title')}</Heading>
            <Text mb={2}>{t('setupProfile.description')}</Text>
            {error && <Text color="red.500" mb={4}>{error}</Text>}
            <form onSubmit={handleSubmit}>
                <Stack spacing={4}>
                    {/* Personal Info */}
                    {['first_name', 'last_name', 'date_of_birth'].map((field) => (
                        <FormControl key={field} isRequired>
                            <FormLabel>{t(`setupProfile.form.${field}.label`)}</FormLabel>
                            <Input
                                name={field}
                                type={field === 'date_of_birth' ? 'date' : 'text'}
                                value={formData[field]}
                                onChange={(e) => handleFieldChange(field, e.target.value)}
                            />
                        </FormControl>
                    ))}

                    {/* Gender */}
                    <FormControl isRequired>
                        <FormLabel>{t('setupProfile.form.gender.label')}</FormLabel>
                        <Select
                            name="gender"
                            value={formData.gender}
                            onChange={(e) => handleFieldChange('gender', e.target.value)}
                            placeholder={t('setupProfile.form.gender.placeholder')}
                        >
                            {['male', 'female', 'other'].map((opt) => (
                                <option key={opt} value={opt}>
                                    {t(`setupProfile.form.gender.options.${opt}`)}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Measurements */}
                    <WeightInput
                        weight={formData.weight}
                        weightUnit={formData.weightUnit}
                        onChange={handleFieldChange}
                    />
                    <HeightInput
                        heightUnit={formData.heightUnit}
                        height={formData.height}
                        feet={formData.feet}
                        inches={formData.inches}
                        onChange={handleFieldChange}
                    />

                    {/* Dietary */}
                    <FormControl>
                        <FormLabel>{t('setupProfile.form.dietaryRestrictions.label')}</FormLabel>
                        <Input
                            name="dietary_restrictions"
                            value={formData.dietary_restrictions}
                            onChange={(e) => handleFieldChange('dietary_restrictions', e.target.value)}
                        />
                    </FormControl>

                    {/* Tags */}
                    <TagInput
                        label={t('setupProfile.form.currentMedications.label')}
                        tags={formData.current_medications}
                        onAdd={addTag('current_medications')}
                        onRemove={removeTag('current_medications')}
                    />
                    <TagInput
                        label={t('setupProfile.form.preExistingConditions.label')}
                        tags={formData.pre_existing_conditions}
                        onAdd={addTag('pre_existing_conditions')}
                        onRemove={removeTag('pre_existing_conditions')}
                    />
                    <TagInput
                        label={t('setupProfile.form.fitnessGoals.label')}
                        tags={formData.fitness_goals}
                        onAdd={addTag('fitness_goals')}
                        onRemove={removeTag('fitness_goals')}
                    />

                    <Button type="submit" colorScheme="teal" isLoading={loading}>
                        {t('setupProfile.form.submit')}
                    </Button>
                </Stack>
            </form>
        </Box>
    );
};

export default SetupProfilePage;
